import React, { useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { DollarSign, Edit, Save, X } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../App';

const AdminPricing = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.admin_sub_role === 'superadmin';
  
  const [tradiesTiers, setTradiesTiers] = useState([
    // Sole Trader Tiers
    { id: 'sole_basic', name: 'Sole Trader - Basic', code: 'SOLE_BASIC', business_type: 'sole_trader', price: 0, max_listings: 3, description: 'Perfect for individuals getting started', video_length_sec: 0, max_videos: 0, highlight_search: false, show_logo: false },
    { id: 'sole_premium', name: 'Sole Trader - Premium', code: 'SOLE_PREMIUM', business_type: 'sole_trader', price: 29, max_listings: 10, description: 'More visibility for sole traders', video_length_sec: 60, max_videos: 3, highlight_search: true, show_logo: true },
    { id: 'sole_pro', name: 'Sole Trader - Pro', code: 'SOLE_PRO', business_type: 'sole_trader', price: 49, max_listings: 25, description: 'Maximum features for professionals', video_length_sec: 180, max_videos: 10, highlight_search: true, show_logo: true },
    // Company Tiers
    { id: 'company_basic', name: 'Company - Basic', code: 'COMPANY_BASIC', business_type: 'company', price: 49, max_listings: 5, description: 'Basic plan for small companies', video_length_sec: 60, max_videos: 5, highlight_search: false, show_logo: true },
    { id: 'company_premium', name: 'Company - Premium', code: 'COMPANY_PREMIUM', business_type: 'company', price: 99, max_listings: 20, description: 'Enhanced visibility for growing companies', video_length_sec: 180, max_videos: 15, highlight_search: true, show_logo: true },
    { id: 'company_pro', name: 'Company - Pro', code: 'COMPANY_PRO', business_type: 'company', price: 199, max_listings: 50, description: 'Unlimited features for established companies', video_length_sec: 300, max_videos: 50, highlight_search: true, show_logo: true },
  ]);
  
  const [jobsTiers, setJobsTiers] = useState([
    { id: 'basic_job', name: 'Basic Job Ad', code: 'BASIC_JOB', price: 99, duration_days: 30, max_active: 5, highlight: false },
    { id: 'standard_job', name: 'Standard Job Ad', code: 'STANDARD_JOB', price: 199, duration_days: 60, max_active: 20, highlight: true },
    { id: 'premium_job', name: 'Premium Job Ad', code: 'PREMIUM_JOB', price: 399, duration_days: 90, max_active: null, highlight: true },
  ]);
  
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});

  const handleEdit = (tier, type) => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admin can edit pricing');
      return;
    }
    setEditing({ id: tier.id, type });
    setEditData(tier);
  };

  const handleSave = () => {
    toast.success('Pricing updated (demo mode)');
    
    if (editing.type === 'tradies') {
      setTradiesTiers(prev => prev.map(t => t.id === editing.id ? editData : t));
    } else {
      setJobsTiers(prev => prev.map(t => t.id === editing.id ? editData : t));
    }
    
    setEditing(null);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Pricing Management</h1>
            <p className="text-muted-foreground">Manage subscription tiers and pricing</p>
          </div>
          {!isSuperAdmin && (
            <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
              Read-Only
            </Badge>
          )}
        </div>

        {/* GoTradies Pricing */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-blue-600" />
            GoTradies Subscription Tiers
          </h2>
          
          {/* Sole Trader Tiers */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Sole Trader Plans</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {tradiesTiers.filter(t => t.business_type === 'sole_trader').map((tier) => (
              <Card key={tier.id}>
                <CardHeader>
                  <div className="flex justify-between">
                    <div>
                      <CardTitle>{tier.name}</CardTitle>
                      <Badge className="mt-2">{tier.code}</Badge>
                    </div>
                    {isSuperAdmin && editing?.id !== tier.id && (
                      <button onClick={() => handleEdit(tier, 'tradies')} className="p-2 hover:bg-muted rounded">
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {editing?.id === tier.id ? (
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-medium text-muted-foreground block mb-1">Price (AUD/month)</label>
                        <Input type="number" value={editData.price} onChange={(e) => setEditData({ ...editData, price: parseFloat(e.target.value) })} />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground block mb-1">Max Active Listings</label>
                        <Input type="number" value={editData.max_listings} onChange={(e) => setEditData({ ...editData, max_listings: parseInt(e.target.value) })} />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground block mb-1">Video Length (seconds)</label>
                        <Input type="number" value={editData.video_length_sec || 0} onChange={(e) => setEditData({ ...editData, video_length_sec: parseInt(e.target.value) })} />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground block mb-1">Max Videos</label>
                        <Input type="number" value={editData.max_videos || 0} onChange={(e) => setEditData({ ...editData, max_videos: parseInt(e.target.value) })} />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground block mb-1">Description</label>
                        <Textarea value={editData.description} onChange={(e) => setEditData({ ...editData, description: e.target.value })} rows={2} />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={handleSave} className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">
                          <Save className="w-4 h-4 inline mr-2" />Save
                        </button>
                        <button onClick={() => setEditing(null)} className="flex-1 border px-4 py-2 rounded-lg hover:bg-muted">
                          <X className="w-4 h-4 inline mr-2" />Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="text-3xl font-bold text-center py-4">${tier.price}<span className="text-lg text-muted-foreground">/mo</span></div>
                      <p className="text-sm text-muted-foreground mb-3">{tier.description}</p>
                      <div className="text-sm">
                        <div className="flex justify-between py-1">
                          <span className="text-muted-foreground">Max Listings:</span>
                          <span className="font-medium">{tier.max_listings}</span>
                        </div>
                        <div className="flex justify-between py-1">
                          <span className="text-muted-foreground">Video Length:</span>
                          <span className="font-medium">{tier.video_length_sec}s</span>
                        </div>
                        <div className="flex justify-between py-1">
                          <span className="text-muted-foreground">Max Videos:</span>
                          <span className="font-medium">{tier.max_videos}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          </div>
          
          {/* Company Tiers */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Company Plans</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {tradiesTiers.filter(t => t.business_type === 'company').map((tier) => (
                <Card key={tier.id}>
                  <CardHeader>
                    <div className="flex justify-between">
                      <div>
                        <CardTitle>{tier.name}</CardTitle>
                        <Badge className="mt-2">{tier.code}</Badge>
                      </div>
                      {isSuperAdmin && editing?.id !== tier.id && (
                        <button onClick={() => handleEdit(tier, 'tradies')} className="p-2 hover:bg-muted rounded">
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {editing?.id === tier.id ? (
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs font-medium text-muted-foreground block mb-1">Price (AUD/month)</label>
                          <Input type="number" value={editData.price} onChange={(e) => setEditData({ ...editData, price: parseFloat(e.target.value) })} />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground block mb-1">Max Active Listings</label>
                          <Input type="number" value={editData.max_listings} onChange={(e) => setEditData({ ...editData, max_listings: parseInt(e.target.value) })} />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground block mb-1">Video Length (seconds)</label>
                          <Input type="number" value={editData.video_length_sec || 0} onChange={(e) => setEditData({ ...editData, video_length_sec: parseInt(e.target.value) })} />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground block mb-1">Max Videos</label>
                          <Input type="number" value={editData.max_videos || 0} onChange={(e) => setEditData({ ...editData, max_videos: parseInt(e.target.value) })} />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground block mb-1">Description</label>
                          <Textarea value={editData.description} onChange={(e) => setEditData({ ...editData, description: e.target.value })} rows={2} />
                        </div>
                        <div className="flex gap-2">
                          <button onClick={handleSave} className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">
                            <Save className="w-4 h-4 inline mr-2" />Save
                          </button>
                          <button onClick={() => setEditing(null)} className="flex-1 border px-4 py-2 rounded-lg hover:bg-muted">
                            <X className="w-4 h-4 inline mr-2" />Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="text-3xl font-bold text-center py-4">${tier.price}<span className="text-lg text-muted-foreground">/mo</span></div>
                        <p className="text-sm text-muted-foreground mb-3">{tier.description}</p>
                        <div className="text-sm space-y-1">
                          <div className="flex justify-between py-1">
                            <span className="text-muted-foreground">Max Listings:</span>
                            <span className="font-medium">{tier.max_listings}</span>
                          </div>
                          <div className="flex justify-between py-1">
                            <span className="text-muted-foreground">Video Length:</span>
                            <span className="font-medium">{tier.video_length_sec}s</span>
                          </div>
                          <div className="flex justify-between py-1">
                            <span className="text-muted-foreground">Max Videos:</span>
                            <span className="font-medium">{tier.max_videos}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* GoJobs Pricing */}
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-purple-600" />
            GoJobs Ad Tiers
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {jobsTiers.map((tier) => (
              <Card key={tier.id}>
                <CardHeader>
                  <div className="flex justify-between">
                    <div>
                      <CardTitle>{tier.name}</CardTitle>
                      <Badge className="mt-2">{tier.code}</Badge>
                    </div>
                    {isSuperAdmin && editing?.id !== tier.id && (
                      <button onClick={() => handleEdit(tier, 'jobs')} className="p-2 hover:bg-muted rounded">
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {editing?.id === tier.id ? (
                    <div className="space-y-3">
                      <Input type="number" value={editData.price} onChange={(e) => setEditData({ ...editData, price: parseFloat(e.target.value) })} placeholder="Price" />
                      <Input type="number" value={editData.duration_days} onChange={(e) => setEditData({ ...editData, duration_days: parseInt(e.target.value) })} placeholder="Duration (days)" />
                      <Input type="number" value={editData.max_active || ''} onChange={(e) => setEditData({ ...editData, max_active: e.target.value ? parseInt(e.target.value) : null })} placeholder="Max Active (blank = unlimited)" />
                      <div className="flex gap-2">
                        <button onClick={handleSave} className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">
                          <Save className="w-4 h-4 inline mr-2" />Save
                        </button>
                        <button onClick={() => setEditing(null)} className="flex-1 border px-4 py-2 rounded-lg hover:bg-muted">
                          <X className="w-4 h-4 inline mr-2" />Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="text-3xl font-bold text-center py-4">${tier.price}</div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Duration:</span>
                          <span className="font-medium">{tier.duration_days} days</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Max Active:</span>
                          <span className="font-medium">{tier.max_active || 'Unlimited'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Highlight:</span>
                          <span>{tier.highlight ? '✓' : '—'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminPricing;
